import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { Button } from 'ui/shared/components/Button/Button';
import { useAuth } from '../../hooks/useAuth';
import { useAuthStore } from 'ui/shared/hooks/useAuthStore';
import styles from './SignInModal.module.scss';

export const SignInModal: React.FC = () => {
  const { isLoginModalOpen, closeLoginModal, authError } = useAuthStore();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isMounted, setIsMounted] = useState(false);
  const { login, isLoading, error: loginError } = useAuth();

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLoginModal();
    };

    if (isLoginModalOpen) {
      window.addEventListener('keydown', handleEscape);
      // Prevent scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isLoginModalOpen, closeLoginModal]);

  if (!isLoginModalOpen || !isMounted) return null;

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const success = await login(username, password);
    if (success) {
      closeLoginModal();
    }
    // Reset form
    setUsername('');
    setPassword('');
  };

  const modalContent = (
    <dialog
      className={`${styles.overlay} ${styles.modal}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) closeLoginModal();
      }}
      open
      aria-labelledby="signin-title"
      data-testid="signin-overlay"
      role="dialog"
    >
      <div className={styles.modalInner}>
        <div className={styles.header}>
          <h2 id="signin-title" className={styles.title}>
            Sign In
          </h2>
          <button
            onClick={closeLoginModal}
            className={styles.closeButton}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {authError && <div className={styles.error}>{authError}</div>}
        {loginError && <div className={styles.error}>{loginError}</div>}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="username" className={styles.label}>
              Username
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={styles.input}
              required
              autoFocus
              disabled={isLoading}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              required
              disabled={isLoading}
            />
          </div>

          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              onClick={closeLoginModal}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </div>
        </form>
      </div>
    </dialog>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
